import { useMarketingViewSection, useMarketingViewRestoring } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Download, Loader2, Pencil, Plus, RotateCcw, Share2, Trash2 } from 'lucide-react';
import { channelWideTableApi, type ChannelWideTableMonitorParams, type ChannelWideTableRow } from '../api/channelWideTable';
import { ColumnToggle } from '../components/ColumnToggle';
import { MultiSelect } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import { alignMarketingDateRange, getCurrentCalendarRange, CHANNEL_WIDE_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { useAppStore } from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';
import { confirmDialog } from '../store/useConfirmStore';
import './ChannelWideTable.css';

type ReportTab = 'channel' | 'utm';
type TableTab = 'tree' | 'dimension';
type DateGrain = 'day' | 'week' | 'month' | 'quarter' | 'halfYear' | 'year';
type DatePreset = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'thisWeek' | 'thisMonth' | 'custom';
type MetricGroupKey = 'platform' | 'onsite' | 'onsiteNew' | 'onsiteOld' | 'segment';

const TREE_PAGE_SIZE = 10;
// 渠道宽表的两种视图统一按 10 条分页：仅按时间查看 30 天时，也应提供
// 明确的翻页入口，不能因为切到动态维度就将 30 天全部折叠为单页。
const DYNAMIC_DIMENSION_PAGE_SIZE = 10;
const TREE_VIRTUAL_ROW_HEIGHT = 44;
const TREE_VIRTUAL_OVERSCAN = 10;
const TABLE_VIEWPORT_MAX_HEIGHT = 560;
const TABLE_VIEWPORT_MIN_HEIGHT = 160;
const TABLE_HEADER_HEIGHT = 74;
// 固定表格占满可视区时，分页栏会被推到首屏之外；为其预留与样式一致的高度。
const TABLE_PAGINATION_HEIGHT = 54;

interface FilterDef {
  key: string;
  label: string;
  help?: string;
}

interface DimensionDef {
  key: string;
  label: string;
}

interface DrillGroupDef {
  key: string;
  label: string;
  dimensionKeys: string[];
}

interface MetricDef {
  key: string;
  label: string;
  section: MetricGroupKey;
}

interface MetricGroupDef {
  key: MetricGroupKey;
  label: string;
}

interface TreeDisplayRow {
  id: string;
  ancestors: string[];
  label: string;
  depth: number;
  dimensionKey: string;
  hasChildren: boolean;
  // 结构节点由明细路径补齐时没有可用的 CUBE 指标，不能把子节点值当作父节点值。
  hasMetrics: boolean;
  metrics?: Record<string, number>;
}

interface SavedTableView {
  id: string;
  name: string;
  hiddenMetricKeys: string[];
}

interface SharedTableViewPayload {
  version: 1;
  reportTab: ReportTab;
  view: Pick<SavedTableView, 'id' | 'name' | 'hiddenMetricKeys'>;
}

interface AppliedChannelWideQuery {
  params: ChannelWideTableMonitorParams;
  exportDimensionKeys: string[];
}

const DATE_GRAINS: Array<{ value: DateGrain; label: string }> = [
  { value: 'day', label: '日' }, { value: 'week', label: '周' }, { value: 'month', label: '月' },
  { value: 'quarter', label: '季度' }, { value: 'halfYear', label: '半年' }, { value: 'year', label: '年' },
];

const CHANNEL_FILTERS: FilterDef[] = [
  { key: 'channelL1', label: '一级渠道' }, { key: 'channelL2', label: '二级渠道' }, { key: 'channelL3', label: '三级渠道' },
  { key: 'segmentL1', label: '一级客群' }, { key: 'segmentL2', label: '二级客群' }, { key: 'segmentL3', label: '三级客群' },
  { key: 'objective', label: '投放目标' }, { key: 'objectiveDetail', label: '投放目标明细' },
];

const UTM_FILTERS: FilterDef[] = [
  { key: 'utmSource', label: '来源', help: '选择来源/媒介/Campaign筛选时，广告平台数据展示位为“-”，其他数据正常展示' },
  { key: 'utmMedium', label: '媒介', help: '选择来源/媒介/Campaign筛选时，广告平台数据展示位为“-”，其他数据正常展示' },
  { key: 'utmCampaign', label: 'Campaign', help: '选择来源/媒介/Campaign筛选时，广告平台数据展示位为“-”，其他数据正常展示' },
  { key: 'objective', label: '投放目标' }, { key: 'objectiveDetail', label: '投放目标明细' },
];

const CHANNEL_DIMENSIONS: DimensionDef[] = [
  { key: 'time', label: '时间' },
  ...CHANNEL_FILTERS.map(({ key, label }) => ({ key, label })),
];

const UTM_DIMENSIONS: DimensionDef[] = [
  { key: 'time', label: '时间' },
  ...UTM_FILTERS.map(({ key, label }) => ({ key, label })),
];

const SEGMENT_L2_OPTIONS_BY_L1: Record<string, string[]> = {
  '当月注册': ['纯潜客-当月新客', '首单-当月新客'],
  '当年非当月注册': ['首单-当年非当月新客'],
  '非当年注册': ['历史年-注册新客'],
  '新客复购': ['当年新复购'],
  '往年老客': ['往年老复购'],
};

const SEGMENT_L3_OPTIONS_BY_L2: Record<string, string[]> = {
  '纯潜客-当月新客': [
    '当月新客-纯潜客-含Engaged', '当月新客-纯潜客-排Engaged', '当月新客-首单-互动排regist',
  ],
  '首单-当月新客': [
    '当月新客-首单-当月注册未下单-排cart', '当月新客-首单-当月注册未下单-含cart',
    '当月新客-首单-当月浏览未加购-排当月regist', '当月新客-首单-当月浏览未加购-含当月regist',
    '当月新客-首单-当月加购未下单-排当月regist', '当月新客-首单-当月加购未下单-含当月regist',
    '当月新客-首单-当月其他-排当月regist',
  ],
  '首单-当年非当月新客': [
    '非当月新客-首单-最近60天注册未下单用户-排cart-排当月regist', '非当月新客-首单-最近60天注册未下单用户-含cart-排当月regist',
    '非当月新客-首单-最近90天注册未下单用户-排当月regist', '非当月新客-首单-最近180天注册未下单用户-排当月regist',
    '非当月新客-首单-26年注册未下单用户-排当月regist', '非当月新客-首单-180天浏览且注册但未加购-排当月regist',
    '非当月新客-首单-其他-排当月regist',
  ],
  '历史年-注册新客': [
    '历史年新客-首单-25年注册未下单用户', '历史年新客-首单-24年注册未下单用户', '历史年新客-首单-23年注册未下单用户',
    '历史年新客-首单-所有注册未下单用户', '历史年新客-首单-其他【其他非当年注册未下单用户】',
  ],
  '当年新复购': [
    '今年_用户分层_A', '今年_用户分层_B', '今年_用户分层_C', '首次下单时间在2026年', '最近100天已下单新用户',
    '首单在当月下单新用户', '其他【当年新复购的其他列表】',
  ],
  '往年老复购': [
    '所有已下单用户', '用户分层_D', '用户分层_E', '用户分层_F', '用户分层_G',
    '首次下单时间在2025年', '首次下单时间在2024年', '首次下单时间在2023年', '首次下单时间在2022年',
    '最近100天已下单老用户', '180天购买', '最近30天下单用户',
    '末次下单时间在2025年', '末次下单时间在2024年', '末次下单时间在2023年', '末次下单时间在2022年', '末次下单时间在18-21年',
    '往年老复购-其他', '老客-其他',
  ],
};

const FILTER_OPTIONS: Record<string, string[]> = {
  channelL1: ['付费广告'],
  channelL2: ['apple_ads', 'applovin_ads', 'bing_ads', 'criteo_ads', 'fb_ads', 'google_ads', 'pinterest_ads', 'rtb_ads', 'snapchat_ads', 'tiktok_ads', 'twitter_ads'],
  channelL3: ['apple_ads', 'applovin_ads', 'bing_ads', 'criteo_ads', 'fb_ads', 'google_ads_brand', 'google_ads_discover', 'google_ads_display', 'google_ads_other', 'google_ads_paidsearch', 'google_ads_pmax', 'google_ads_youtube', 'pinterest_ads', 'rtb_ads', 'snapchat_ads', 'tiktok_ads', 'twitter_ads'],
  segmentL1: Object.keys(SEGMENT_L2_OPTIONS_BY_L1),
  segmentL2: Object.values(SEGMENT_L2_OPTIONS_BY_L1).flat(),
  segmentL3: Object.values(SEGMENT_L3_OPTIONS_BY_L2).flat(),
  objective: ['other', '加购', '引流', '曝光', '注册', '转化'],
  objectiveDetail: ['applovin', 'applovin_ads', 'bing_ads', 'criteo', 'criteo_ads', 'fb上层-流量-落地页浏览量最大化', 'fb上层-知名度-覆盖人数最大化', 'fb下层', 'fb中层-加购', 'fb中层-注册', 'gg上层-video', 'gg下层-discovery', 'gg下层-display', 'gg下层-max', 'gg下层-other', 'gg下层-search', 'gg下层-youtube', 'gg中层-discovery', 'gg中层-display', 'google_ads_brand', 'other', 'rtb', 'rtb_ads', 'snapchat上层-流量-web', 'snapchat上层-认知度-最大故事打开', 'snapchat上层-认知度-最大曝光', 'snapchat上层-认知度-最大点击', 'snapchat下层', 'snapchat中层-加购', 'snapchat中层-注册', 'tt上层-覆盖人数-7天3次', 'tt上层-视频播放量-6svv', 'tt上层-访问量-落地页', 'tt下层', 'tt中层-加购', 'tt中层-注册', 'twitter'],
};

const UTM_TEXT_FILTER_KEYS = new Set(['utmSource', 'utmMedium', 'utmCampaign']);

const CHANNEL_L3_OPTIONS_BY_L2: Record<string, string[]> = {
  apple_ads: ['apple_ads'],
  applovin_ads: ['applovin_ads'],
  bing_ads: ['bing_ads'],
  criteo_ads: ['criteo_ads'],
  fb_ads: ['fb_ads'],
  google_ads: ['google_ads_brand', 'google_ads_discover', 'google_ads_display', 'google_ads_other', 'google_ads_paidsearch', 'google_ads_pmax', 'google_ads_youtube'],
  pinterest_ads: ['pinterest_ads'],
  rtb_ads: ['rtb_ads'],
  snapchat_ads: ['snapchat_ads'],
  tiktok_ads: ['tiktok_ads'],
  twitter_ads: ['twitter_ads'],
};

const METRIC_GROUPS: MetricGroupDef[] = [
  { key: 'platform', label: '广告平台数据' }, { key: 'onsite', label: '站内效果数据' }, { key: 'onsiteNew', label: '新客数据' },
  { key: 'onsiteOld', label: '老客数据' }, { key: 'segment', label: '客群数据' },
];

const PLATFORM_METRICS = [
  ['spend', '账户花费'], ['impr', '展示次数'], ['clicks', '点击次数'], ['atc', '加购次数'], ['purchases', '购物次数'], ['purchaseValue', '购物转化价值'], ['regs', '完成注册次数'], ['cpm', '账户CPM'], ['ctr', '账户CTR'], ['cpc', '账户CPC'], ['costPerAtc', '单次加购费用'], ['cpa', '账户CPA'], ['roi', '账户ROI'], ['aov', '账户单均价'], ['clickAtcRate', '点击加购率'], ['cartCvr', '购物车转化率'], ['clickPurchaseRate', '点击购买率'],
] as const;

const ONSITE_METRICS = [
  ['visitUv', '访问UV'], ['uvCost', '访问UV成本'], ['revenuePerUv', '单UV收益'], ['visitPurchaseRate', '访购率'], ['uvReachRate', 'UV到达率'], ['regUsers', '注册用户数'], ['regCac', '注册CAC'], ['sameDayRegRate', '当日注册率'], ['regFirstOrderRate', '注册首单率'], ['netSales', '净销售额'], ['paidOrders', '支付订单数'], ['paidUsers', '支付用户数'], ['arpu', '客单价'], ['orderAov', '单均价'], ['freq', '频次'], ['channelCac', '渠道CAC'], ['channelCpa', '渠道CPA'], ['channelRoi', '渠道ROI'], ['prospectUv', '未注册访问UV'],
] as const;

const CUSTOMER_EXTRA_METRICS = [
  ['netSales', '净销售额'], ['paidOrders', '支付订单数'], ['paidUsers', '支付用户数'], ['arpu', '客单价'], ['orderAov', '单均价'], ['freq', '频次'], ['paidUserShare', '支付用户数占比'], ['channelCac', '渠道CAC'], ['channelCpa', '渠道CPA'], ['channelRoi', '渠道ROI'],
] as const;

const toMetrics = (items: readonly (readonly [string, string])[], section: MetricGroupKey, prefix = ''): MetricDef[] => items.map(([key, label]) => ({ key: `${prefix}${key}`, label, section }));
const METRICS: MetricDef[] = [
  ...toMetrics(PLATFORM_METRICS, 'platform'),
  ...toMetrics(ONSITE_METRICS, 'onsite'),
  ...toMetrics([...PLATFORM_METRICS, ...CUSTOMER_EXTRA_METRICS], 'onsiteNew', 'new_'),
  ...toMetrics([...PLATFORM_METRICS, ...CUSTOMER_EXTRA_METRICS], 'onsiteOld', 'old_'),
  ...toMetrics([['userShare', '客群用户数占比'], ['segProspectUv', '未注册访问UV']], 'segment'),
];

const COLUMN_TOGGLE_COLS = METRICS.map(metric => ({
  ...metric,
  section: METRIC_GROUPS.find(group => group.key === metric.section)?.label,
}));

// 两个口径首次进入时都仅展示时间；其他下钻/动态维度由用户按需添加。
const CHANNEL_DEFAULT_DIMENSION_KEYS = ['time'];
const UTM_DEFAULT_DIMENSION_KEYS = ['time'];
const CHANNEL_DRILL_GROUPS: DrillGroupDef[] = [
  { key: 'time', label: '时间', dimensionKeys: ['time'] },
  { key: 'channel', label: '渠道', dimensionKeys: ['channelL1', 'channelL2', 'channelL3'] },
  { key: 'segment', label: '客群', dimensionKeys: ['segmentL1', 'segmentL2', 'segmentL3'] },
  { key: 'objective', label: '目标', dimensionKeys: ['objective', 'objectiveDetail'] },
];
const UTM_DRILL_GROUPS: DrillGroupDef[] = [
  { key: 'time', label: '时间', dimensionKeys: ['time'] },
  { key: 'utm', label: 'UTM', dimensionKeys: ['utmSource', 'utmMedium', 'utmCampaign'] },
  { key: 'objective', label: '目标', dimensionKeys: ['objective', 'objectiveDetail'] },
];
const EMPTY_FILTERS: Record<string, string[]> = Object.fromEntries([...CHANNEL_FILTERS, ...UTM_FILTERS].map(item => [item.key, []]));

function normalizeSingleFilters(filters: Record<string, string[]>): Record<string, string[]> {
  return Object.fromEntries(Object.keys(EMPTY_FILTERS).map(key => [key, (filters[key] ?? []).slice(0, 1)]));
}

const API_DIMENSION_KEYS: Record<string, string> = {
  time: 'time',
  channelL1: 'one_level_channel', channelL2: 'two_level_channel', channelL3: 'three_level_channel',
  segmentL1: 'one_level_customer_group', segmentL2: 'two_level_customer_group', segmentL3: 'three_level_customer_group',
  objective: 'delivery_target', objectiveDetail: 'delivery_target_detail',
  utmSource: 'utm_source', utmMedium: 'utm_medium', utmCampaign: 'utm_campaign',
};

const PERCENT_METRIC_SUFFIXES = ['ctr', 'clickAtcRate', 'cartCvr', 'clickPurchaseRate', 'visitPurchaseRate', 'uvReachRate', 'sameDayRegRate', 'regFirstOrderRate', 'paidUserShare', 'userShare'];
const SYSTEM_VIEW_ID = 'system-default';
// 单日、多维度 CUBE 结果可能有数万节点；一次渲染全部指标列会阻塞浏览器主线程。
const TREE_EXPAND_ALL_MAX_NODES = 600;
const ONSITE_METRIC_KEYS = new Set(METRICS.filter(metric => metric.section === 'onsite').map(metric => metric.key));
const PLATFORM_METRIC_KEYS = new Set(METRICS.filter(metric => metric.section === 'platform').map(metric => metric.key));
const CUSTOMER_DETAIL_DIMENSION_KEYS = new Set(['segmentL2', 'segmentL3']);
const UTM_DIMENSION_KEYS = new Set(['utmSource', 'utmMedium', 'utmCampaign']);

function drillGroupsForReport(reportTab: ReportTab) {
  return reportTab === 'channel' ? CHANNEL_DRILL_GROUPS : UTM_DRILL_GROUPS;
}

function normalizeDrillGroupOrder(groupOrder: string[], groups: DrillGroupDef[]) {
  const known = new Set(groups.map(group => group.key));
  const ordered = groupOrder.filter((key, index) => known.has(key) && groupOrder.indexOf(key) === index);
  return [...ordered, ...groups.map(group => group.key).filter(key => !ordered.includes(key))];
}

function orderedDrillKeys(selectedKeys: string[], groupOrder: string[], groups: DrillGroupDef[]) {
  const selected = new Set(selectedKeys);
  const byKey = new Map(groups.map(group => [group.key, group]));
  return normalizeDrillGroupOrder(groupOrder, groups)
    .flatMap(key => byKey.get(key)?.dimensionKeys ?? [])
    .filter(key => selected.has(key));
}

function tableViewStorageKey(brandId: string, siteId: string, reportTab: ReportTab) {
  return `channel-wide-table:views:${brandId}:${siteId}:${reportTab}`;
}

function tableViewLastStorageKey(brandId: string, siteId: string, reportTab: ReportTab) {
  return `channel-wide-table:last-view:${brandId}:${siteId}:${reportTab}`;
}

function parseSavedViews(value: string | null): SavedTableView[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is SavedTableView => Boolean(item && typeof item === 'object' && typeof (item as SavedTableView).id === 'string' && typeof (item as SavedTableView).name === 'string' && Array.isArray((item as SavedTableView).hiddenMetricKeys)))
      .map(item => ({ ...item, hiddenMetricKeys: item.hiddenMetricKeys.filter(key => typeof key === 'string') }));
  } catch {
    return [];
  }
}

function parseSharedTableView(value: string | null): SharedTableViewPayload | null {
  if (!value || value.length > 12_000) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;
    const payload = parsed as SharedTableViewPayload;
    if (payload.version !== 1 || (payload.reportTab !== 'channel' && payload.reportTab !== 'utm') || !payload.view || typeof payload.view.name !== 'string' || !Array.isArray(payload.view.hiddenMetricKeys)) return null;
    return {
      version: 1,
      reportTab: payload.reportTab,
      view: {
        id: typeof payload.view.id === 'string' ? payload.view.id : '',
        name: payload.view.name.trim().slice(0, 30) || '共享视图',
        hiddenMetricKeys: payload.view.hiddenMetricKeys.filter(key => typeof key === 'string'),
      },
    };
  } catch {
    return null;
  }
}

function uniqueTableViewName(views: SavedTableView[], name: string) {
  if (!views.some(view => view.name === name)) return name;
  const base = `${name}（共享）`.slice(0, 30);
  if (!views.some(view => view.name === base)) return base;
  for (let index = 2; index < 100; index += 1) {
    const candidate = `${base.slice(0, Math.max(1, 28 - String(index).length))} ${index}`;
    if (!views.some(view => view.name === candidate)) return candidate;
  }
  return `${base.slice(0, 22)} ${Date.now().toString().slice(-5)}`;
}

function daysAgo(days: number) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function yesterday() {
  return daysAgo(1);
}

function getDateRange(preset: DatePreset, customStart: string, customEnd: string) {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, daysAgo(0));
  if (preset === 'today') return { start: daysAgo(0), end: daysAgo(0) };
  if (preset === 'yesterday') return { start: yesterday(), end: yesterday() };
  if (preset === '90d') return { start: daysAgo(90), end: yesterday() };
  if (preset === '30d') return { start: daysAgo(30), end: yesterday() };
  return { start: daysAgo(7), end: yesterday() };
}

function periodStart(value: string, grain: DateGrain) {
  const date = dateFromDimensionValue(value) ?? dateFromDimensionValue(yesterday());
  if (!date) return value;
  switch (grain) {
    case 'week':
      date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
      break;
    case 'month':
      date.setUTCDate(1);
      break;
    case 'quarter':
      date.setUTCMonth(Math.floor(date.getUTCMonth() / 3) * 3, 1);
      break;
    case 'halfYear':
      date.setUTCMonth(date.getUTCMonth() < 6 ? 0 : 6, 1);
      break;
    case 'year':
      date.setUTCMonth(0, 1);
      break;
    default:
      break;
  }
  return formatYmd(date);
}

function formatMetric(key: string, value: number | undefined) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return '-';
  if (PERCENT_METRIC_SUFFIXES.some(suffix => key === suffix || key.endsWith(`_${suffix}`))) return `${(number * 100).toFixed(2)}%`;
  return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function ensureDynamicTimeDimension(keys: string[]) {
  return ['time', ...keys.filter((key, index) => key !== 'time' && keys.indexOf(key) === index)];
}

function orderedDynamicDimensionKeys(keys: string[], dimensions: DimensionDef[]) {
  const selected = new Set(ensureDynamicTimeDimension(keys));
  return dimensions.map(dimension => dimension.key).filter(key => selected.has(key));
}

function isDetailDimensionValue(value: string | undefined) {
  return Boolean(value && value !== '全部');
}

function shouldDisplayMetricDash(reportTab: ReportTab, metricKey: string, tableDimensions: DimensionDef[], treeDimensionKey?: string) {
  if (treeDimensionKey) {
    if (reportTab === 'channel') return ONSITE_METRIC_KEYS.has(metricKey) && CUSTOMER_DETAIL_DIMENSION_KEYS.has(treeDimensionKey);
    // UTM 树形下钻仅在最低层是来源、媒介或 Campaign 时隐藏该最低层的广告平台指标；
    // 上层汇总仍按各自 CUBE 行展示，避免误隐藏投放目标等维度的广告数据。
    const minimumDimensionKey = tableDimensions[tableDimensions.length - 1]?.key;
    return reportTab === 'utm' && PLATFORM_METRIC_KEYS.has(metricKey) && treeDimensionKey === minimumDimensionKey && UTM_DIMENSION_KEYS.has(minimumDimensionKey);
  }
  const minimumDimensionKey = tableDimensions[tableDimensions.length - 1]?.key;
  if (reportTab === 'channel' && ONSITE_METRIC_KEYS.has(metricKey)) {
    // 动态维度按固定业务顺序的最小维度决定口径，而非按点击顺序或是否曾勾选过。
    return Boolean(minimumDimensionKey && CUSTOMER_DETAIL_DIMENSION_KEYS.has(minimumDimensionKey));
  }
  if (reportTab === 'utm' && PLATFORM_METRIC_KEYS.has(metricKey)) {
    // UTM 来源、媒介、Campaign 维度对应站内归因口径；动态维度只要带有其中
    // 任一项，广告平台指标都不具备可比性，统一展示为 “-”。
    return tableDimensions.some(dimension => UTM_DIMENSION_KEYS.has(dimension.key));
  }
  return false;
}

function displayDimensionValue(value: string | undefined) {
  return value?.trim() || '未标记';
}

function dateFromDimensionValue(value: string) {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!matched) return null;
  const date = new Date(Date.UTC(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatYmd(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function isoWeekOf(date: Date) {
  const thursday = new Date(date);
  thursday.setUTCDate(thursday.getUTCDate() + 3 - ((thursday.getUTCDay() + 6) % 7));
  const weekYear = thursday.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(weekYear, 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 3 - ((firstThursday.getUTCDay() + 6) % 7));
  return {
    year: weekYear,
    week: 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000)),
  };
}

function formatDimensionValue(key: string, value: string, dateGrain: DateGrain) {
  if (key !== 'time' || !value) return value || '未标记';
  const date = dateFromDimensionValue(value);
  if (!date) return value;

  switch (dateGrain) {
    case 'week': {
      const monday = new Date(date);
      monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
      const sunday = new Date(monday);
      sunday.setUTCDate(sunday.getUTCDate() + 6);
      const { year, week } = isoWeekOf(monday);
      return `${year}年第${week}周(${formatYmd(monday)}到${formatYmd(sunday)})`;
    }
    case 'month':
      return value.slice(0, 7);
    case 'quarter':
      return `${date.getUTCFullYear()}第${Math.floor(date.getUTCMonth() / 3) + 1}季度`;
    case 'halfYear':
      return `${date.getUTCFullYear()}${date.getUTCMonth() < 6 ? '上半年' : '下半年'}`;
    case 'year':
      return String(date.getUTCFullYear());
    default:
      return value;
  }
}

function buildTreeRows(rows: ChannelWideTableRow[], dimensions: DimensionDef[], reportTab: ReportTab): TreeDisplayRow[] {
  type Node = Omit<TreeDisplayRow, 'hasChildren' | 'ancestors'> & { parentID?: string; childIDs: string[]; metricRollupScore?: number };
  const nodes = new Map<string, Node>();
  rows.forEach(row => {
    const values = dimensions.map(dimension => row.dimensions?.[API_DIMENSION_KEYS[dimension.key] ?? dimension.key] ?? '');
    const isUTMTargetRow = reportTab === 'utm' &&
      (isDetailDimensionValue(row.dimensions?.delivery_target) || isDetailDimensionValue(row.dimensions?.delivery_target_detail));
    // "全部" 是 CUBE 汇总行；但 UTM 以投放目标/明细为维度时，来源、媒介、
    // Campaign 的 "全部" 只是无归因占位，需要作为 "-" 展示并继续保留目标层级。
    const isRollup = (value: string, dimensionIndex: number) => value === '全部' &&
      !(isUTMTargetRow && UTM_DIMENSION_KEYS.has(dimensions[dimensionIndex].key));
    // CUBE "全部" 表示当前路径在这一层聚合结束。因此一旦遇到第一个
    // "全部"，后续值只是筛选上下文，不能再跨过该层拼接为子节点（例如
    // 客群="全部" 后的投放目标="转化"不能挂在三级客群下面）。
    const firstRollupIndex = values.findIndex(isRollup);
    const visibleValues = firstRollupIndex < 0 ? values : values.slice(0, firstRollupIndex);
    const displayLevels = visibleValues.map((value, dimensionIndex) => ({ value, dimensionIndex }));

    if (displayLevels.length === 0) {
      // 全局 CUBE 汇总行同样不在树中展示。
      return;
    }

    // 补齐可见的真实维度前缀；未勾选维度由后端按“全部”过滤，不会出现在路径中。
    for (let depth = 0; depth < displayLevels.length; depth += 1) {
      const id = displayLevels.slice(0, depth + 1).map(level => `${level.dimensionIndex}:${level.value}`).join('/');
      if (nodes.has(id)) continue;
      const parentID = depth === 0
        ? 'cube-total'
        : displayLevels.slice(0, depth).map(level => `${level.dimensionIndex}:${level.value}`).join('/');
      const level = displayLevels[depth];
      nodes.set(id, {
        id,
        label: displayDimensionValue(level.value),
        depth: depth + 1,
        dimensionKey: dimensions[level.dimensionIndex].key,
        hasMetrics: false,
        parentID,
        childIDs: [],
      });
    }
    const applyMetrics = (nodeID: string, hierarchyDepth: number) => {
      const metricNode = nodes.get(nodeID);
      if (!metricNode) return;
      // 同一节点可能被多条不同层级的 CUBE 行命中。优先选取该节点之后
      // “全部”更多的原始 CUBE 汇总行；绝不在前端累计子级指标。
      const rollupScore = values.slice(hierarchyDepth).filter((value, index) => isRollup(value, hierarchyDepth + index)).length;
      if (!metricNode.hasMetrics || rollupScore >= (metricNode.metricRollupScore ?? -1)) {
        metricNode.metrics = row.metrics ?? {};
        metricNode.hasMetrics = true;
        metricNode.metricRollupScore = rollupScore;
      }
    };
    // 无论谁处于一级，都要由同一条 Holo CUBE 原始行补齐它及其父级的
    // 指标。这样一级节点不会因为它的汇总行出现在更深层 CUBE 坐标中而
    // 显示为“-”。
    for (let depth = 1; depth <= displayLevels.length; depth += 1) {
      applyMetrics(displayLevels.slice(0, depth).map(level => `${level.dimensionIndex}:${level.value}`).join('/'), depth);
    }
  });

  const rootIDs: string[] = [];
  nodes.forEach(node => {
    const parent = node.parentID ? nodes.get(node.parentID) : undefined;
    if (parent) parent.childIDs.push(node.id);
    else rootIDs.push(node.id);
  });
  const flattened: TreeDisplayRow[] = [];
  const visit = (id: string, ancestors: string[]) => {
    const node = nodes.get(id);
    if (!node) return;
    flattened.push({ ...node, ancestors, hasChildren: node.childIDs.length > 0 });
    node.childIDs.forEach(childID => visit(childID, [...ancestors, node.id]));
  };
  rootIDs.forEach(id => visit(id, []));
  return flattened;
}

function treeDimensionBadgeLabel(key: string, dimensions: DimensionDef[]) {
  const label = dimensions.find(dimension => dimension.key === key)?.label ?? '';
  return label.replace(/维度$/, '').replace('投放目标', '目标');
}

function FilterSelect({ definition, value, options, onChange }: { definition: FilterDef; value: string[]; options?: string[]; onChange: (value: string[]) => void }) {
  const isUTMTextFilter = UTM_TEXT_FILTER_KEYS.has(definition.key);
  return (
    <label className="channel-wide-filter-field">
      <span className="data-filter-label channel-wide-filter-label">
        {definition.label}
        {definition.help && <span className="channel-wide-help" aria-label={definition.help}>?</span>}
      </span>
      {isUTMTextFilter ? (
        <input
          className="form-input"
          value={value[0] ?? ''}
          onChange={event => onChange(event.target.value.trim() ? [event.target.value] : [])}
          placeholder="输入关键词，支持模糊搜索"
        />
      ) : (
        <MultiSelect single clearable searchable options={(options ?? FILTER_OPTIONS[definition.key] ?? []).map(item => ({ value: item, label: item }))} value={value} onChange={onChange} placeholder="全部" style={{ width: '100%' }} />
      )}
    </label>
  );
}

function SaveTableViewDialog({ title = '另存为视图', confirmText = '保存', value, error, onChange, onCancel, onConfirm }: { title?: string; confirmText?: string; value: string; error: string; onChange: (value: string) => void; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="modal-overlay open" onClick={onCancel}>
      <div className="modal channel-wide-save-view-modal" role="dialog" aria-modal="true" aria-labelledby="channel-wide-save-view-title" onClick={event => event.stopPropagation()}>
        <div className="modal-header"><h3 id="channel-wide-save-view-title">{title}</h3><button type="button" className="modal-close" aria-label="关闭" onClick={onCancel}>×</button></div>
        <div className="modal-body channel-wide-save-view-body"><label className="form-label" htmlFor="channel-wide-view-name">视图名称</label><input id="channel-wide-view-name" className="form-input" value={value} maxLength={30} autoFocus aria-invalid={Boolean(error)} placeholder="例如：渠道核心指标" onChange={event => onChange(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') onConfirm(); }} />{error && <span className="channel-wide-view-name-error">{error}</span>}</div>
        <div className="modal-footer"><button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>取消</button><button type="button" className="btn btn-primary btn-sm" onClick={onConfirm}>{confirmText}</button></div>
      </div>
    </div>
  );
}

function TableViewMenu({ currentViewID, views, onSelect, onSaveAs, onRename, onShare, onDelete }: {
  currentViewID: string;
  views: SavedTableView[];
  onSelect: (id: string) => void;
  onSaveAs: () => void;
  onRename: (view: SavedTableView) => void;
  onShare: (view: SavedTableView) => void;
  onDelete: (view: SavedTableView) => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedName = currentViewID === SYSTEM_VIEW_ID ? '系统默认' : views.find(view => view.id === currentViewID)?.name ?? '系统默认';

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={menuRef} className="channel-wide-view-menu">
      <button type="button" className={`channel-wide-view-trigger${open ? ' open' : ''}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)}>
        <span>{selectedName}</span><ChevronDown size={14} />
      </button>
      {open && <div className="channel-wide-view-dropdown" role="menu" aria-label="表格视图">
        <button type="button" role="menuitem" className={`channel-wide-view-option${currentViewID === SYSTEM_VIEW_ID ? ' selected' : ''}`} onClick={() => { onSelect(SYSTEM_VIEW_ID); setOpen(false); }}><span>系统默认</span>{currentViewID === SYSTEM_VIEW_ID && <span className="channel-wide-view-check">✓</span>}</button>
        {views.map(view => <div key={view.id} className={`channel-wide-view-option channel-wide-view-option-saved${currentViewID === view.id ? ' selected' : ''}`}>
          <button type="button" role="menuitem" className="channel-wide-view-name" onClick={() => { onSelect(view.id); setOpen(false); }} title={view.name}>{view.name}</button>
          <span className="channel-wide-view-actions">
            <button type="button" aria-label={`重命名${view.name}`} title="重命名" onClick={() => { onRename(view); setOpen(false); }}><Pencil size={12} /></button>
            <button type="button" aria-label={`分享${view.name}`} title="分享" onClick={() => { onShare(view); setOpen(false); }}><Share2 size={12} /></button>
            <button type="button" aria-label={`删除${view.name}`} title="删除" className="danger" onClick={() => { onDelete(view); setOpen(false); }}><Trash2 size={12} /></button>
          </span>
          {currentViewID === view.id && <span className="channel-wide-view-check">✓</span>}
        </div>)}
        <div className="channel-wide-view-dropdown-footer"><button type="button" onClick={() => { onSaveAs(); setOpen(false); }}><Plus size={13} />另存为视图</button></div>
      </div>}
    </div>
  );
}

function DrillConfig({ dimensions, groups, draftKeys, groupOrder, mandatoryKeys, onChange, onGroupOrderChange, onCancel, onApply, onReset }: {
  dimensions: DimensionDef[];
  groups: DrillGroupDef[];
  draftKeys: string[];
  groupOrder: string[];
  mandatoryKeys: string[];
  onChange: (keys: string[]) => void;
  onGroupOrderChange: (groupOrder: string[]) => void;
  onCancel: () => void;
  onApply: () => void;
  onReset: () => void;
}) {
  const toggle = (key: string) => {
    if (mandatoryKeys.includes(key)) return;
    onChange(draftKeys.includes(key) ? draftKeys.filter(item => item !== key) : [...draftKeys, key]);
  };
  const dimensionsByKey = useMemo(() => new Map(dimensions.map(dimension => [dimension.key, dimension])), [dimensions]);
  const orderedGroups = useMemo(() => {
    const byKey = new Map(groups.map(group => [group.key, group]));
    return normalizeDrillGroupOrder(groupOrder, groups).map(key => byKey.get(key)).filter((group): group is DrillGroupDef => Boolean(group));
  }, [groupOrder, groups]);
  const moveGroup = (key: string, direction: -1 | 1) => {
    const index = groupOrder.indexOf(key);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= groupOrder.length) return;
    const next = [...groupOrder];
    [next[index], next[target]] = [next[target], next[index]];
    onGroupOrderChange(next);
  };
  const selectedCount = draftKeys.length;

  return (
    <div className="modal-overlay open" onClick={onCancel}>
      <div className="modal modal-lg channel-wide-drill-modal" role="dialog" aria-modal="true" aria-labelledby="channel-wide-drill-modal-title" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 id="channel-wide-drill-modal-title">下钻配置</h3>
            <p className="channel-wide-drill-modal-description">组内维度可自由勾选，顺序固定；仅支持调整不同分组之间的下钻顺序。</p>
          </div>
          <button type="button" className="modal-close" aria-label="关闭下钻配置" onClick={onCancel}>×</button>
        </div>
        <div className="modal-body channel-wide-drill-modal-body">
          <div className="channel-wide-drill-column">
            <div className="channel-wide-drill-column-title">可用维度</div>
            <div className="channel-wide-drill-available-list">
              {orderedGroups.map(group => (
                <div key={group.key} className="channel-wide-drill-available-group">
                  <div className="channel-wide-drill-available-group-title">{group.label}</div>
                  {group.dimensionKeys.map(key => dimensionsByKey.get(key)).filter((dimension): dimension is DimensionDef => Boolean(dimension)).map(dimension => {
                    const selected = draftKeys.includes(dimension.key);
                    const mandatory = mandatoryKeys.includes(dimension.key);
                    return (
                      <label key={dimension.key} className={`channel-wide-drill-available-item${selected ? ' selected' : ''}`}>
                        <input type="checkbox" checked={selected} disabled={mandatory} onChange={() => toggle(dimension.key)} />
                        <span>{dimension.label}{mandatory ? '（必选）' : ''}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="channel-wide-drill-column">
            <div className="channel-wide-drill-column-title">下钻分组 <span>{selectedCount} 个维度</span></div>
            <div className="channel-wide-drill-order-list">
              {selectedCount === 0 && <div className="channel-wide-drill-order-empty">请从左侧选择至少一个维度</div>}
              {orderedGroups.map((group, position) => {
                const selectedDimensions = group.dimensionKeys
                  .map(key => dimensionsByKey.get(key))
                  .filter((dimension): dimension is DimensionDef => Boolean(dimension))
                  .filter(dimension => draftKeys.includes(dimension.key));
                const last = orderedGroups.length - 1;
                return (
                  <div key={group.key} className={`channel-wide-drill-order-item channel-wide-drill-group-order-item${selectedDimensions.length > 0 ? ' selected' : ''}`}>
                    <span className="channel-wide-drill-order-number">{position + 1}</span>
                    <div className="channel-wide-drill-order-name"><strong>{group.label}</strong><span>{selectedDimensions.length > 0 ? selectedDimensions.map(dimension => dimension.label).join(' → ') : '未选择维度'}</span></div>
                    <div className="channel-wide-drill-order-actions">
                      <button type="button" aria-label={`上移${group.label}分组`} disabled={position === 0} onClick={() => moveGroup(group.key, -1)}><ChevronUp size={13} /></button>
                      <button type="button" aria-label={`下移${group.label}分组`} disabled={position === last} onClick={() => moveGroup(group.key, 1)}><ChevronDown size={13} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="modal-footer channel-wide-drill-modal-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onReset}>恢复默认</button>
          <span />
          <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>取消</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onApply} disabled={draftKeys.length === 0}>应用</button>
        </div>
      </div>
    </div>
  );
}

function DataTable({
  tableTab, reportTab, dateGrain, dimensions, hiddenMetrics, rows, treeRows, expandedTreeNodeIDs, onToggleTreeNode, loading, error, queried, viewportHeight, pagination,
}: {
  tableTab: TableTab;
  reportTab: ReportTab;
  dateGrain: DateGrain;
  dimensions: DimensionDef[];
  hiddenMetrics: Set<string>;
  rows: ChannelWideTableRow[];
  treeRows: TreeDisplayRow[];
  expandedTreeNodeIDs: Set<string>;
  onToggleTreeNode: (id: string) => void;
  loading: boolean;
  error: string;
  queried: boolean;
  /** 表格内部滚动视口的真实可用高度。 */
  viewportHeight: number;
  /** 固定表格容器内的分页栏，随表格一起停靠。 */
  pagination?: React.ReactNode;
}) {
  const visibleMetrics = METRICS.filter(metric => !hiddenMetrics.has(metric.key));
  const metricGroups = METRIC_GROUPS.map(group => ({ ...group, metrics: visibleMetrics.filter(metric => metric.section === group.key) })).filter(group => group.metrics.length > 0);
  const visibleDimensions = tableTab === 'tree' ? [] : (dimensions.length > 0 ? dimensions : [{ key: 'empty', label: '未选择维度' }]);
  const colSpan = visibleMetrics.length + (tableTab === 'tree' ? 1 : visibleDimensions.length);
  const visibleTreeRows = useMemo(
    () => treeRows.filter(row => row.ancestors.every(id => expandedTreeNodeIDs.has(id))),
    [expandedTreeNodeIDs, treeRows],
  );
  // 仅根据当前实际显示的列设置最小宽度；此前固定为 2560px，即使只显示两列指标也会出现无意义横向滚动。
  const tableMinWidth = (tableTab === 'tree' ? 340 : visibleDimensions.reduce((width, dimension) => width + (dimension.key === 'time' && dateGrain === 'week' ? 270 : 160), 0)) + visibleMetrics.length * 120;
  const tableColumnWidths = [
    ...(tableTab === 'tree' ? [340] : visibleDimensions.map(dimension => dimension.key === 'time' && dateGrain === 'week' ? 270 : 160)),
    ...visibleMetrics.map(() => 120),
  ];
  const dragRef = useRef<{ pointerID: number; startX: number; startScrollLeft: number; nextScrollLeft: number; frameID: number | null } | null>(null);
  const pageScrollTransferRef = useRef<{ viewport: HTMLElement | null; velocityY: number; frameID: number | null }>({ viewport: null, velocityY: 0, frameID: null });
  const tableHeaderWrapRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const tableDockRef = useRef<HTMLDivElement>(null);
  const tableDockPlaceholderRef = useRef<HTMLDivElement>(null);
  const [isTableDragging, setIsTableDragging] = useState(false);
  const [treeScrollTop, setTreeScrollTop] = useState(0);
  const [dockLayout, setDockLayout] = useState({ fixed: false, top: 0, left: 0, width: 0 });
  const hasPagination = pagination != null;
  const dockHeight = viewportHeight + (hasPagination ? TABLE_PAGINATION_HEIGHT : 0);
  const tableBodyViewportHeight = Math.max(0, viewportHeight - TABLE_HEADER_HEIGHT);
  const treeMaxScrollTop = Math.max(0, visibleTreeRows.length * TREE_VIRTUAL_ROW_HEIGHT - tableBodyViewportHeight);
  const effectiveTreeScrollTop = Math.min(treeScrollTop, treeMaxScrollTop);
  const treeVirtualStart = tableTab === 'tree'
    ? Math.max(0, Math.floor(effectiveTreeScrollTop / TREE_VIRTUAL_ROW_HEIGHT) - TREE_VIRTUAL_OVERSCAN)
    : 0;
  const treeVirtualEnd = tableTab === 'tree'
    ? Math.min(visibleTreeRows.length, Math.ceil((effectiveTreeScrollTop + tableBodyViewportHeight) / TREE_VIRTUAL_ROW_HEIGHT) + TREE_VIRTUAL_OVERSCAN)
    : 0;
  const virtualTreeRows = tableTab === 'tree' ? visibleTreeRows.slice(treeVirtualStart, treeVirtualEnd) : [];
  const treeTopSpacerHeight = treeVirtualStart * TREE_VIRTUAL_ROW_HEIGHT;
  const treeBottomSpacerHeight = Math.max(0, (visibleTreeRows.length - treeVirtualEnd) * TREE_VIRTUAL_ROW_HEIGHT);

  useEffect(() => {
    // 新查询结果从顶部开始；展开/收起仍保持当前位置，避免用户浏览时跳回首行。
    setTreeScrollTop(0);
    if (tableWrapRef.current) tableWrapRef.current.scrollTop = 0;
  }, [treeRows]);
  useEffect(() => () => {
    const transfer = pageScrollTransferRef.current;
    if (transfer.frameID !== null) window.cancelAnimationFrame(transfer.frameID);
  }, []);
  const startTableDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.target instanceof Element && event.target.closest('button, input, a, td, th')) return;
    const scrollContainer = event.currentTarget;
    const bounds = scrollContainer.getBoundingClientRect();
    const horizontalScrollbarHeight = scrollContainer.offsetHeight - scrollContainer.clientHeight;
    const verticalScrollbarWidth = scrollContainer.offsetWidth - scrollContainer.clientWidth;
    // 底部横向滚动条由浏览器原生处理。若在这里捕获 Pointer，松开时
    // endTableDrag 会把 scrollLeft 还原为拖动前的值，从而跳回最左侧。
    const hitsHorizontalScrollbar = horizontalScrollbarHeight > 0 && event.clientY >= bounds.bottom - horizontalScrollbarHeight;
    const hitsVerticalScrollbar = verticalScrollbarWidth > 0 && event.clientX >= bounds.right - verticalScrollbarWidth;
    if (hitsHorizontalScrollbar || hitsVerticalScrollbar) return;
    const maxScrollLeft = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
    if (maxScrollLeft === 0) return;
    dragRef.current = { pointerID: event.pointerId, startX: event.clientX, startScrollLeft: scrollContainer.scrollLeft, nextScrollLeft: scrollContainer.scrollLeft, frameID: null };
    scrollContainer.setPointerCapture(event.pointerId);
    setIsTableDragging(true);
  };
  const moveTableDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerID !== event.pointerId) return;
    event.preventDefault();
    const maxScrollLeft = Math.max(0, event.currentTarget.scrollWidth - event.currentTarget.clientWidth);
    drag.nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, drag.startScrollLeft - (event.clientX - drag.startX)));
    if (drag.frameID !== null) return;
    const scrollContainer = event.currentTarget;
    drag.frameID = window.requestAnimationFrame(() => {
      const activeDrag = dragRef.current;
      if (!activeDrag || activeDrag.pointerID !== drag.pointerID) return;
      const maxScrollLeft = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
      scrollContainer.scrollLeft = Math.max(0, Math.min(maxScrollLeft, activeDrag.nextScrollLeft));
      activeDrag.frameID = null;
    });
  };
  const endTableDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerID !== event.pointerId) return;
    if (drag.frameID !== null) window.cancelAnimationFrame(drag.frameID);
    const maxScrollLeft = Math.max(0, event.currentTarget.scrollWidth - event.currentTarget.clientWidth);
    event.currentTarget.scrollLeft = Math.max(0, Math.min(maxScrollLeft, drag.nextScrollLeft));
    dragRef.current = null;
    setIsTableDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const onTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (tableHeaderWrapRef.current && tableHeaderWrapRef.current.scrollLeft !== event.currentTarget.scrollLeft) {
      tableHeaderWrapRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
    if (tableTab !== 'tree') return;
    // React 状态更新回调执行时 SyntheticEvent 的 currentTarget 已失效，
    // 必须在事件同步阶段读取滚动位置。
    const nextScrollTop = event.currentTarget.scrollTop;
    setTreeScrollTop(current => current === nextScrollTop ? current : nextScrollTop);
  };
  const onHeaderScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (tableWrapRef.current && tableWrapRef.current.scrollLeft !== event.currentTarget.scrollLeft) {
      tableWrapRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };
  const onTableWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    // 横向滚动和 Ctrl 缩放保持浏览器默认行为，只转交普通纵向滚轮。
    if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    const tableViewport = event.currentTarget;
    const atTop = tableViewport.scrollTop <= 0;
    const atBottom = tableViewport.scrollTop + tableViewport.clientHeight >= tableViewport.scrollHeight - 1;
    const shouldPassToPage = (event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom);
    if (!shouldPassToPage) return;

    const pageViewport = tableViewport.closest<HTMLElement>('.content');
    if (!pageViewport) return;
    const canScrollUp = event.deltaY < 0 && pageViewport.scrollTop > 0;
    const canScrollDown = event.deltaY > 0 && pageViewport.scrollTop + pageViewport.clientHeight < pageViewport.scrollHeight - 1;
    if (!canScrollUp && !canScrollDown) return;

    event.preventDefault();
    const deltaMultiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pageViewport.clientHeight : 1;
    const transfer = pageScrollTransferRef.current;
    if (transfer.viewport !== pageViewport) {
      if (transfer.frameID !== null) window.cancelAnimationFrame(transfer.frameID);
      transfer.viewport = pageViewport;
      transfer.velocityY = 0;
      transfer.frameID = null;
    }
    const normalizedDelta = event.deltaY * deltaMultiplier;
    // 方向反转时立即停止上一段惯性，避免顶部/底部回拉。
    if (transfer.velocityY !== 0 && Math.sign(transfer.velocityY) !== Math.sign(normalizedDelta)) transfer.velocityY = 0;
    // 24% 的输入在后续动画帧以 0.76 衰减，累计位移仍约等于原始滚轮位移。
    transfer.velocityY += normalizedDelta * 0.24;
    if (transfer.frameID !== null) return;
    const transferPageScroll = () => {
      const activeTransfer = pageScrollTransferRef.current;
      if (!activeTransfer.viewport || Math.abs(activeTransfer.velocityY) < 0.25) {
        activeTransfer.velocityY = 0;
        activeTransfer.frameID = null;
        return;
      }
      activeTransfer.viewport.scrollTop += activeTransfer.velocityY;
      activeTransfer.velocityY *= 0.76;
      activeTransfer.frameID = window.requestAnimationFrame(transferPageScroll);
    };
    transfer.frameID = window.requestAnimationFrame(transferPageScroll);
  };
  const renderColumnGroup = () => <colgroup>{tableColumnWidths.map((width, index) => <col key={index} style={{ width }} />)}</colgroup>;
  const renderTableHeader = () => (
    <thead>
      <tr className="channel-wide-group-row">
        {tableTab === 'tree'
          ? <th rowSpan={2} className="channel-wide-dimension-heading">下钻维度</th>
          : <th colSpan={visibleDimensions.length} className="channel-wide-dimension-group">维度</th>}
        {metricGroups.map(group => <th key={group.key} colSpan={group.metrics.length} className={`channel-wide-metric-group channel-wide-group-${group.key}`}>{group.label}</th>)}
      </tr>
      <tr className="channel-wide-metric-row">
        {tableTab === 'dimension' && visibleDimensions.map(dimension => <th key={dimension.key} className={`channel-wide-dynamic-heading${dimension.key === 'time' && dateGrain === 'week' ? ' channel-wide-week-heading' : ''}`}>{dimension.label}</th>)}
        {metricGroups.flatMap(group => group.metrics.map((metric, index) => <th key={metric.key} className={`channel-wide-metric-heading channel-wide-metric-${group.key}${index === group.metrics.length - 1 ? ' group-end' : ''}`}><MetricDescriptionTooltip label={metric.label} /></th>))}
      </tr>
    </thead>
  );

  useLayoutEffect(() => {
    const dock = tableDockRef.current;
    const placeholder = tableDockPlaceholderRef.current;
    const scrollRoot = dock?.closest<HTMLElement>('.content');
    const filterArea = document.querySelector<HTMLElement>('.channel-wide-page .dashboard-sticky-filters');
    if (!dock || !placeholder || !scrollRoot || !filterArea) return;

    const updateDockLayout = () => {
      const filterRect = filterArea.getBoundingClientRect();
      const placeholderRect = placeholder.getBoundingClientRect();
      // 不使用 CSS sticky：其父级高度不足时会把第一行表头压到筛选区下方。
      // 到达筛选区后保持固定，数据体继续独立滚动；占位块保证页面布局不跳动。
      const fixed = placeholderRect.top <= filterRect.bottom;
      const next = {
        fixed,
        top: Math.round(filterRect.bottom),
        left: Math.round(placeholderRect.left),
        width: Math.round(placeholderRect.width),
      };
      setDockLayout(current => current.fixed === next.fixed && current.top === next.top && current.left === next.left && current.width === next.width ? current : next);
    };

    updateDockLayout();
    const observer = new ResizeObserver(updateDockLayout);
    observer.observe(dock);
    observer.observe(placeholder);
    observer.observe(filterArea);
    observer.observe(scrollRoot);
    scrollRoot.addEventListener('scroll', updateDockLayout, { passive: true });
    window.addEventListener('resize', updateDockLayout);
    return () => {
      observer.disconnect();
      scrollRoot.removeEventListener('scroll', updateDockLayout);
      window.removeEventListener('resize', updateDockLayout);
    };
  }, [dockHeight, dockLayout.fixed, viewportHeight]);

  const tableDockStyle = dockLayout.fixed
    ? { position: 'fixed' as const, top: dockLayout.top, left: dockLayout.left, width: dockLayout.width, height: dockHeight }
    : { position: 'relative' as const, height: dockHeight };

  return (
    <>
      <div ref={tableDockPlaceholderRef} className="channel-wide-table-dock-placeholder" style={{ height: dockLayout.fixed ? dockHeight : 0 }} aria-hidden="true" />
      <div ref={tableDockRef} className="channel-wide-table-sticky" style={tableDockStyle}>
      <div ref={tableHeaderWrapRef} className={`channel-wide-table-header-wrap${isTableDragging ? ' is-dragging' : ''}`} onScroll={onHeaderScroll} onPointerDown={startTableDrag} onPointerMove={moveTableDrag} onPointerUp={endTableDrag} onPointerCancel={endTableDrag}>
      <table className="channel-wide-table" style={{ width: '100%', minWidth: tableMinWidth }}>
        {renderColumnGroup()}
        {renderTableHeader()}
      </table>
      </div>
      <div ref={tableWrapRef} className={`channel-wide-table-wrap${isTableDragging ? ' is-dragging' : ''}`} onScroll={onTableScroll} onWheel={onTableWheel} onPointerDown={startTableDrag} onPointerMove={moveTableDrag} onPointerUp={endTableDrag} onPointerCancel={endTableDrag}>
      <table className="channel-wide-table channel-wide-table-body" style={{ width: '100%', minWidth: tableMinWidth }}>
        {renderColumnGroup()}
        <tbody>
          {loading && rows.length === 0 ? (
            <tr className="channel-wide-placeholder-row channel-wide-initial-loading-row"><td colSpan={colSpan}><TableLoadingCell /></td></tr>
          ) : error ? (
            <tr className="channel-wide-placeholder-row"><td colSpan={colSpan}><div className="channel-wide-empty channel-wide-query-error"><strong>查询失败</strong><span>{error}</span></div></td></tr>
          ) : !queried ? (
            <tr className="channel-wide-placeholder-row"><td colSpan={colSpan}><div className="channel-wide-empty"><strong>请设置筛选条件后查询</strong><span>将按当前条件读取渠道宽表数据。</span></div></td></tr>
          ) : (tableTab === 'tree' ? treeRows.length === 0 : rows.length === 0) ? (
            <tr className="channel-wide-placeholder-row"><td colSpan={colSpan}><div className="channel-wide-empty"><strong>暂无数据</strong><span>请调整时间范围或筛选项后重试。</span></div></td></tr>
          ) : tableTab === 'tree' ? <>
            {treeTopSpacerHeight > 0 && <tr className="channel-wide-virtual-spacer" aria-hidden="true"><td colSpan={colSpan} style={{ height: treeTopSpacerHeight }} /></tr>}
            {virtualTreeRows.map(row => (
              <tr key={row.id} className={`channel-wide-data-row channel-wide-tree-row${row.hasChildren ? ' tree-row-parent' : ''}`}>
                <td className="channel-wide-tree-value">
                  <span className="tree-cell-content" style={{ paddingLeft: row.depth * 20 }}>
                    {row.hasChildren ? (
                      <button type="button" className="tree-toggle-btn" onClick={() => onToggleTreeNode(row.id)} aria-label={`${expandedTreeNodeIDs.has(row.id) ? '收起' : '展开'}${row.label}`} aria-expanded={expandedTreeNodeIDs.has(row.id)}>{expandedTreeNodeIDs.has(row.id) ? '−' : '+'}</button>
                    ) : (
                      <span className="tree-toggle-spacer" aria-hidden="true" />
                    )}
                    {treeDimensionBadgeLabel(row.dimensionKey, dimensions) && <span className="channel-wide-tree-dimension-tag">{treeDimensionBadgeLabel(row.dimensionKey, dimensions)}</span>}
                    <span className="tree-cell-value" title={formatDimensionValue(row.dimensionKey, row.label, dateGrain)}>{formatDimensionValue(row.dimensionKey, row.label, dateGrain)}</span>
                  </span>
                </td>
                {metricGroups.flatMap(group => group.metrics.map(metric => (
                  <td key={metric.key} className={`channel-wide-metric-value channel-wide-value-${group.key}`}>{row.hasMetrics && !shouldDisplayMetricDash(reportTab, metric.key, dimensions, row.dimensionKey) ? formatMetric(metric.key, row.metrics?.[metric.key]) : '-'}</td>
                )))}
              </tr>
            ))}
            {treeBottomSpacerHeight > 0 && <tr className="channel-wide-virtual-spacer" aria-hidden="true"><td colSpan={colSpan} style={{ height: treeBottomSpacerHeight }} /></tr>}
          </> : rows.map((row, index) => (
            <tr key={`${row.dimensions?.time ?? 'row'}-${index}`} className="channel-wide-data-row">
              {visibleDimensions.map(dimension => {
                const rawValue = displayDimensionValue(row.dimensions?.[API_DIMENSION_KEYS[dimension.key] ?? dimension.key]);
                const value = formatDimensionValue(dimension.key, rawValue, dateGrain);
                return <td key={dimension.key} className={`channel-wide-dimension-value${dimension.key === 'time' && dateGrain === 'week' ? ' channel-wide-week-value' : ''}`}><span className="channel-wide-dimension-text" title={value}>{value}</span></td>;
              })}
              {metricGroups.flatMap(group => group.metrics.map(metric => (
                <td key={metric.key} className={`channel-wide-metric-value channel-wide-value-${group.key}`}>{shouldDisplayMetricDash(reportTab, metric.key, dimensions) ? '-' : formatMetric(metric.key, row.metrics?.[metric.key])}</td>
              )))}
            </tr>
          ))}
        </tbody>
      </table>
        {loading && rows.length > 0 && <TableLoadingOverlay text="正在查询…" subtext="正在读取渠道宽表数据" />}
      </div>
      {pagination}
      </div>
    </>
  );
}

export default function ChannelWideTable() {
  const isRestoringView = useMarketingViewRestoring();
  const { brandId, siteId } = useAppStore();
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const [reportTab, setReportTab] = useState<ReportTab>('channel');
  const [tableTab, setTableTab] = useState<TableTab>('tree');
  const [dateGrain, setDateGrain] = useState<DateGrain>('day');
  // 筛选中的粒度与当前已查询结果的粒度分离：切换筛选不会重解释旧列表中的时间值。
  const [appliedDateGrain, setAppliedDateGrain] = useState<DateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => yesterday());
  const [filters, setFilters] = useState<Record<string, string[]>>(EMPTY_FILTERS);
  const [dynamicDimensionKeys, setDynamicDimensionKeys] = useState<string[]>(CHANNEL_DEFAULT_DIMENSION_KEYS);
  const [drillDimensionKeys, setDrillDimensionKeys] = useState<string[]>(CHANNEL_DEFAULT_DIMENSION_KEYS);
  const [drillDraftKeys, setDrillDraftKeys] = useState<string[]>(CHANNEL_DEFAULT_DIMENSION_KEYS);
  const [drillGroupOrder, setDrillGroupOrder] = useState<string[]>(() => CHANNEL_DRILL_GROUPS.map(group => group.key));
  const [drillGroupOrderDraft, setDrillGroupOrderDraft] = useState<string[]>(() => CHANNEL_DRILL_GROUPS.map(group => group.key));
  const [drillConfigOpen, setDrillConfigOpen] = useState(false);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());
  const [savedViews, setSavedViews] = useState<SavedTableView[]>([]);
  const [currentViewID, setCurrentViewID] = useState(SYSTEM_VIEW_ID);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [editingViewID, setEditingViewID] = useState<string | null>(null);
  const [pendingSharedViewQueryTab, setPendingSharedViewQueryTab] = useState<ReportTab | null>(null);
  const [viewNameDraft, setViewNameDraft] = useState('');
  const [viewNameError, setViewNameError] = useState('');
  const [rows, setRows] = useState<ChannelWideTableRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSizes, setPageSizes] = useState<Record<TableTab, number>>({ tree: TREE_PAGE_SIZE, dimension: DYNAMIC_DIMENSION_PAGE_SIZE });
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [queryError, setQueryError] = useState('');
  const [exportError, setExportError] = useState('');
  const [exportSubmitting, setExportSubmitting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [appliedQuery, setAppliedQuery] = useState<AppliedChannelWideQuery | null>(null);
  const [expandedTreeNodeIDs, setExpandedTreeNodeIDs] = useState<Set<string>>(new Set());
  const filterAreaRef = useRef<HTMLDivElement>(null);
  // 筛选项仍由用户手动提交；仅 Tab、展示维度和下钻配置变化后自动按新口径查询。
  const pendingAutoQueryRef = useRef(false);
  const initialQueryKeyRef = useRef('');
  const sharedViewHandledRef = useRef('');
  const [autoQueryVersion, setAutoQueryVersion] = useState(0);
  const [tableViewport, setTableViewport] = useState({ height: TABLE_VIEWPORT_MAX_HEIGHT });
  const currentPageSize = pageSizes[tableTab];
  // total_page 由接口返回；再按 total 兜底，避免服务端旧版本遗漏 total_page 时
  // 动态维度的翻页入口被错误隐藏。
  const resolvedTotalPage = Math.max(1, totalPage, Math.ceil(total / currentPageSize));
  const shouldShowPagination = queried && !queryError;

  useLayoutEffect(() => {
    const filterArea = filterAreaRef.current;
    const scrollRoot = filterArea?.closest<HTMLElement>('.content');
    if (!filterArea || !scrollRoot) return;

    // 不能用筛选区自身高度推算：它使用负 margin/top 扩展到内容区边缘。
    // 这里始终以筛选区的真实可见底边为准，保证表头恰好停在筛选项下方。
    const updateViewport = () => {
      const filterRect = filterArea.getBoundingClientRect();
      const rootRect = scrollRoot.getBoundingClientRect();
      // DataTable 固定后由占位块撑开文档流；若不扣除分页高度，分页栏会刚好
      // 落在视口之外。树形与动态维度共用这一布局，因此必须统一预留。
      const footerReserve = shouldShowPagination ? TABLE_PAGINATION_HEIGHT : 0;
      const availableHeight = Math.floor(rootRect.bottom - filterRect.bottom - 16 - footerReserve);
      const nextHeight = Math.min(TABLE_VIEWPORT_MAX_HEIGHT, Math.max(TABLE_VIEWPORT_MIN_HEIGHT, availableHeight));
      setTableViewport(current => current.height === nextHeight
        ? current
        : { height: nextHeight });
    };

    updateViewport();
    const observer = new ResizeObserver(updateViewport);
    observer.observe(filterArea);
    observer.observe(scrollRoot);
    scrollRoot.addEventListener('scroll', updateViewport, { passive: true });
    window.addEventListener('resize', updateViewport);
    return () => {
      observer.disconnect();
      scrollRoot.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
    };
  }, [shouldShowPagination]);

  const activeFilters = reportTab === 'channel' ? CHANNEL_FILTERS : UTM_FILTERS;
  const activeDimensions = reportTab === 'channel' ? CHANNEL_DIMENSIONS : UTM_DIMENSIONS;
  const channelL3Options = useMemo(() => {
    const selectedL2 = filters.channelL2 ?? [];
    if (selectedL2.length === 0) return FILTER_OPTIONS.channelL3;
    return [...new Set(selectedL2.flatMap(value => CHANNEL_L3_OPTIONS_BY_L2[value] ?? []))];
  }, [filters.channelL2]);
  const segmentL2Options = useMemo(() => {
    const selectedL1 = filters.segmentL1 ?? [];
    if (selectedL1.length === 0) return FILTER_OPTIONS.segmentL2;
    return [...new Set(selectedL1.flatMap(value => SEGMENT_L2_OPTIONS_BY_L1[value] ?? []))];
  }, [filters.segmentL1]);
  const segmentL3Options = useMemo(() => {
    const selectedL1 = filters.segmentL1 ?? [];
    const allowedL2 = selectedL1.length === 0 ? FILTER_OPTIONS.segmentL2 : segmentL2Options;
    const selectedL2 = (filters.segmentL2 ?? []).filter(value => allowedL2.includes(value));
    const sourceL2 = selectedL2.length > 0 ? selectedL2 : allowedL2;
    return [...new Set(sourceL2.flatMap(value => SEGMENT_L3_OPTIONS_BY_L2[value] ?? []))];
  }, [filters.segmentL1, filters.segmentL2, segmentL2Options]);
  const activeDrillGroups = drillGroupsForReport(reportTab);
  const defaultDimensionKeys = reportTab === 'channel' ? CHANNEL_DEFAULT_DIMENSION_KEYS : UTM_DEFAULT_DIMENSION_KEYS;
  const byKey = useMemo(() => new Map(activeDimensions.map(item => [item.key, item])), [activeDimensions]);
  const dynamicSelectableDimensions = activeDimensions;
  const orderedDynamicDimensionKeyList = useMemo(() => orderedDynamicDimensionKeys(dynamicDimensionKeys, dynamicSelectableDimensions), [dynamicDimensionKeys, dynamicSelectableDimensions]);
  const dynamicDimensions = useMemo(() => orderedDynamicDimensionKeyList.map(key => byKey.get(key)).filter((item): item is DimensionDef => Boolean(item)), [byKey, orderedDynamicDimensionKeyList]);
  // 筛选只限制取数，不应移除用户选中的下钻维度。
  const treeDimensionKeys = useMemo(
    () => orderedDrillKeys([...new Set([...drillDimensionKeys, 'time'])], drillGroupOrder, activeDrillGroups),
    [activeDrillGroups, drillDimensionKeys, drillGroupOrder],
  );
  const treeDisplayDimensions = useMemo(() => treeDimensionKeys.map(key => byKey.get(key)).filter((item): item is DimensionDef => Boolean(item)), [byKey, treeDimensionKeys]);
  const treeRows = useMemo(() => buildTreeRows(rows, treeDisplayDimensions, reportTab), [treeDisplayDimensions, reportTab, rows]);
  const expandableTreeNodeIDs = useMemo(() => treeRows.filter(row => row.hasChildren).map(row => row.id), [treeRows]);
  const rootTreeNodeIDs = useMemo(() => treeRows.filter(row => row.depth === 1 && row.hasChildren).map(row => row.id), [treeRows]);
  const canExpandAllTreeNodes = expandableTreeNodeIDs.length <= TREE_EXPAND_ALL_MAX_NODES;
  const dateRange = useMemo(() => alignMarketingDateRange(getDateRange(datePreset, customStart, customEnd), dateGrain), [customEnd, customStart, dateGrain, datePreset]);
  const subtitle = tableTab === 'dimension'
    ? `时间聚合：${DATE_GRAINS.find(item => item.value === appliedDateGrain)?.label ?? '日'} · 已选 ${dynamicDimensions.length} 个动态维度`
    : `时间聚合：${DATE_GRAINS.find(item => item.value === appliedDateGrain)?.label ?? '日'} · 下钻：${treeDisplayDimensions.map(item => item.label).join(' → ') || '未选择维度'}`;
  const viewsStorageKey = useMemo(() => tableViewStorageKey(brandId, siteId, reportTab), [brandId, reportTab, siteId]);
  const lastViewStorageKey = useMemo(() => tableViewLastStorageKey(brandId, siteId, reportTab), [brandId, reportTab, siteId]);
  const currentView = savedViews.find(view => view.id === currentViewID);

  useEffect(() => {
    if (!brandId || !siteId) return;
    const loadedViews = parseSavedViews(window.localStorage.getItem(viewsStorageKey));
    const savedLastViewID = window.localStorage.getItem(lastViewStorageKey);
    const nextView = loadedViews.find(view => view.id === savedLastViewID);
    setSavedViews(loadedViews);
    setCurrentViewID(isRestoringView() ? SYSTEM_VIEW_ID : nextView?.id ?? SYSTEM_VIEW_ID);
    if (!isRestoringView()) setHiddenMetrics(new Set(nextView?.hiddenMetricKeys ?? []));
  }, [brandId, lastViewStorageKey, siteId, viewsStorageKey, isRestoringView]);

  useEffect(() => {
    if (!brandId || !siteId) return;
    const rawPayload = new URLSearchParams(window.location.search).get('shared_view');
    if (!rawPayload || sharedViewHandledRef.current === rawPayload) return;
    sharedViewHandledRef.current = rawPayload;
    const payload = parseSharedTableView(rawPayload);
    const url = new URL(window.location.href);
    url.searchParams.delete('shared_view');
    window.history.replaceState({}, '', url.toString());
    if (!payload) {
      showToast('分享链接无效或已损坏', 'error');
      return;
    }

    const targetViewsStorageKey = tableViewStorageKey(brandId, siteId, payload.reportTab);
    const targetLastViewStorageKey = tableViewLastStorageKey(brandId, siteId, payload.reportTab);
    const targetViews = parseSavedViews(window.localStorage.getItem(targetViewsStorageKey));
    const sharedViewName = uniqueTableViewName(targetViews, payload.view.name);
    const savedSharedView: SavedTableView = { id: `shared-${Date.now()}`, name: sharedViewName, hiddenMetricKeys: payload.view.hiddenMetricKeys };
    const nextViews = [...targetViews, savedSharedView];
    window.localStorage.setItem(targetViewsStorageKey, JSON.stringify(nextViews));
    window.localStorage.setItem(targetLastViewStorageKey, savedSharedView.id);

    if (payload.reportTab === reportTab) {
      setSavedViews(nextViews);
      setCurrentViewID(savedSharedView.id);
      setHiddenMetrics(new Set(savedSharedView.hiddenMetricKeys));
    } else {
      const groups = drillGroupsForReport(payload.reportTab);
      const groupOrder = groups.map(group => group.key);
      const defaults = payload.reportTab === 'channel' ? CHANNEL_DEFAULT_DIMENSION_KEYS : UTM_DEFAULT_DIMENSION_KEYS;
      setReportTab(payload.reportTab);
      setDynamicDimensionKeys(ensureDynamicTimeDimension(defaults));
      setDrillDimensionKeys(orderedDrillKeys(defaults, groupOrder, groups));
      setDrillDraftKeys(defaults);
      setDrillGroupOrder(groupOrder);
      setDrillGroupOrderDraft(groupOrder);
    }
    // 分享链接本身会触发一次查询，避免首次进入页面的自动查询重复执行。
    initialQueryKeyRef.current = `${brandId}:${siteId}`;
    if (payload.reportTab === reportTab) {
      pendingAutoQueryRef.current = true;
      setAutoQueryVersion(version => version + 1);
    } else {
      setPendingSharedViewQueryTab(payload.reportTab);
    }
    showToast('共享视图已保存', 'success');
  }, [brandId, reportTab, showToast, siteId]);

  useEffect(() => {
    if (!pendingSharedViewQueryTab || pendingSharedViewQueryTab !== reportTab) return;
    setPendingSharedViewQueryTab(null);
    pendingAutoQueryRef.current = true;
    setAutoQueryVersion(version => version + 1);
  }, [pendingSharedViewQueryTab, reportTab]);

  useEffect(() => {
    // 默认仅展开顶级节点，避免单日高基数查询一次渲染全部树节点和全部指标单元格。
    setExpandedTreeNodeIDs(new Set(rootTreeNodeIDs));
  }, [rootTreeNodeIDs]);

  const reset = () => {
    setDateGrain('day');
    setDatePreset('7d');
    setCustomStart(daysAgo(7));
    setCustomEnd(yesterday());
    setFilters(EMPTY_FILTERS);
    setDynamicDimensionKeys(ensureDynamicTimeDimension(defaultDimensionKeys));
    pendingAutoQueryRef.current = true;
    setAutoQueryVersion(version => version + 1);
  };

  const selectDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset === 'custom') {
      setCustomStart(current => periodStart(current, dateGrain));
      setCustomEnd(current => periodStart(current, dateGrain));
      return;
    }
    const range = getDateRange(preset, customStart, customEnd);
    setCustomStart(range.start);
    setCustomEnd(range.end);
  };

  const selectDateGrain = (next: DateGrain) => {
    setDateGrain(next);
    if (datePreset === 'custom') {
      setCustomStart(current => periodStart(current, next));
      setCustomEnd(current => periodStart(current, next));
    }
  };

  const updateFilter = (key: string, value: string[]) => {
    setFilters(current => {
      const selectedValue = value.slice(0, 1);
      const next = { ...current, [key]: selectedValue };
      if (key === 'channelL2') {
        const availableL3 = new Set(selectedValue.flatMap(item => CHANNEL_L3_OPTIONS_BY_L2[item] ?? []));
        next.channelL3 = (current.channelL3 ?? []).filter(item => availableL3.has(item));
      }
      if (key === 'segmentL2' && selectedValue.length > 0) {
        const availableL3 = new Set(selectedValue.flatMap(item => SEGMENT_L3_OPTIONS_BY_L2[item] ?? []));
        next.segmentL3 = (current.segmentL3 ?? []).filter(item => availableL3.has(item));
      }
      if (key === 'segmentL1') {
        const availableL2 = new Set(selectedValue.length > 0 ? selectedValue.flatMap(item => SEGMENT_L2_OPTIONS_BY_L1[item] ?? []) : FILTER_OPTIONS.segmentL2);
        next.segmentL2 = (current.segmentL2 ?? []).filter(item => availableL2.has(item));
        const availableL3 = new Set((next.segmentL2.length > 0 ? next.segmentL2 : [...availableL2])
          .flatMap(item => SEGMENT_L3_OPTIONS_BY_L2[item] ?? []));
        next.segmentL3 = (current.segmentL3 ?? []).filter(item => availableL3.has(item));
      }
      return next;
    });
  };

  const updateDynamicDimensions = (keys: string[]) => {
    // 动态维度改变的是 SQL 的 GROUP BY，而不是仅改变前端展示列。
    setDynamicDimensionKeys(ensureDynamicTimeDimension(keys));
    setRows([]);
    setPage(1);
    setTotal(0);
    setTotalPage(1);
    setQueryError('');
    setQueried(false);
    pendingAutoQueryRef.current = true;
    setAutoQueryVersion(version => version + 1);
  };

  const query = useCallback(async (nextPage = 1, nextPageSize = currentPageSize) => {
    if (!brandId || !siteId) {
      setQueryError('品牌或站点尚未加载完成，请稍后重试。');
      setQueried(true);
      return;
    }
    if (!dateRange.start || !dateRange.end || dateRange.end < dateRange.start) {
      const message = '请选择正确的时间范围。';
      setQueryError(message);
      setQueried(true);
      return;
    }

    const dimensionKeys = (tableTab === 'tree' ? treeDimensionKeys : orderedDynamicDimensionKeyList)
      .map(key => API_DIMENSION_KEYS[key])
      .filter(Boolean);
    const requestFilters: Record<string, string> = {};
    Object.entries(filters).forEach(([key, values]) => {
      const apiKey = API_DIMENSION_KEYS[key];
      if (apiKey && values.length > 0) requestFilters[apiKey] = JSON.stringify(values);
    });
    const params: ChannelWideTableMonitorParams = {
      brand_id: brandId,
      site_id: siteId,
      start_date: dateRange.start,
      end_date: dateRange.end,
      date_grain: dateGrain,
      report_tab: reportTab,
      view_mode: tableTab,
      dimensions: dimensionKeys.join(','),
      drill_dimensions: dimensionKeys.filter(key => key !== 'time').join(','),
      page: nextPage,
      // 树形视图按顶级节点分页；每页返回 10 个顶级节点及各自完整子树。
      size: nextPageSize,
      ...requestFilters,
    };

    setLoading(true);
    setQueryError('');
    setQueried(true);
    try {
      const response = await channelWideTableApi.getMonitor(params);
      setRows(response.data?.list ?? []);
      setAppliedDateGrain(dateGrain);
      setAppliedQuery({
        params: { ...params },
        exportDimensionKeys: [...(tableTab === 'tree' ? treeDimensionKeys : orderedDynamicDimensionKeyList)],
      });
      setPage(response.data?.page ?? nextPage);
      setTotal(response.data?.total ?? 0);
      setTotalPage(response.data?.total_page ?? 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : '渠道宽表查询失败，请稍后重试';
      setRows([]);
      setTotal(0);
      setTotalPage(1);
      setAppliedQuery(null);
      setQueryError(message);
    } finally {
      setLoading(false);
    }
  }, [brandId, currentPageSize, dateGrain, dateRange.end, dateRange.start, filters, orderedDynamicDimensionKeyList, reportTab, siteId, tableTab, treeDimensionKeys]);

  useEffect(() => {
    if (!pendingAutoQueryRef.current) return;
    pendingAutoQueryRef.current = false;
    void query();
  }, [autoQueryVersion, query]);

  useEffect(() => {
    if (!brandId || !siteId) return;
    const key = `${brandId}:${siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    void query();
  }, [brandId, query, siteId]);

  useEffect(() => {
    const handleFilterEnter = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.isComposing) return;
      const target = event.target;
      // 使用捕获阶段监听，保证日期与下拉组件内部拦截键盘事件时也能查询。
      if (!(target instanceof HTMLElement) || !filterAreaRef.current?.contains(target)) return;
      if (target.closest('button, [role="button"]')) return;
      event.preventDefault();
      void query();
    };
    document.addEventListener('keydown', handleFilterEnter, true);
    return () => document.removeEventListener('keydown', handleFilterEnter, true);
  }, [query]);

  useMarketingViewSection('page', {
    label: '页面筛选', state: { reportTab, tableTab, dateGrain, datePreset, customStart, customEnd, filters, dynamicDimensionKeys, drillDimensionKeys, drillDraftKeys, drillGroupOrder, drillGroupOrderDraft, hiddenMetrics: [...hiddenMetrics] },
    restore: value => { setReportTab(value.reportTab); setTableTab(value.tableTab); setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); setFilters(normalizeSingleFilters(value.filters)); setDynamicDimensionKeys(value.dynamicDimensionKeys); setDrillDimensionKeys(value.drillDimensionKeys); setDrillDraftKeys(value.drillDraftKeys); setDrillGroupOrder(value.drillGroupOrder); setDrillGroupOrderDraft(value.drillGroupOrderDraft); setHiddenMetrics(new Set(value.hiddenMetrics)); },
    loading: loading, query: () => { void query(1); },
    resolve: value => { if (value.datePreset !== 'today') return value; const range = getDateRange('today', value.customStart, value.customEnd); return { ...value, datePreset: 'custom' as const, customStart: range.start, customEnd: range.end }; },
  });

  const exportFlatTable = useCallback(async () => {
    if (!appliedQuery || exportSubmitting) return;
    const appliedDimensions = (appliedQuery.params.report_tab === 'channel' ? CHANNEL_DIMENSIONS : UTM_DIMENSIONS);
    const appliedColumns = [
      { key: 'date_grain', label: '时间粒度' },
      ...appliedQuery.exportDimensionKeys.map(key => {
        const dimension = appliedDimensions.find(item => item.key === key);
        return { key: API_DIMENSION_KEYS[key] ?? key, label: dimension?.label ?? key };
      }),
      ...METRICS.filter(metric => !hiddenMetrics.has(metric.key)).map(metric => ({ key: metric.key, label: metric.label })),
    ];
    setExportSubmitting(true);
    setExportError('');
    try {
      await enqueueReportExport({
        report_key: 'channel_wide_table',
        title: `渠道宽表-${appliedQuery.params.report_tab === 'utm' ? 'UTM-目标' : '渠道-客群-目标'}（${appliedQuery.params.view_mode === 'tree' ? '树形下钻全量' : '平铺'}）`,
        file_name: `渠道宽表_${appliedQuery.params.report_tab === 'utm' ? 'UTM目标' : '渠道客群目标'}.csv`,
        brand_id: appliedQuery.params.brand_id,
        site_id: appliedQuery.params.site_id,
        params: {
          ...appliedQuery.params,
          // 树形下钻导出仍是平铺明细；后台保留这里配置的维度顺序并全量分页。
          page: 1, size: 200,
        },
        columns: appliedColumns,
      });
    } catch (error) {
      setExportError(error instanceof Error ? error.message : '导出任务提交失败，请稍后重试。');
    } finally {
      setExportSubmitting(false);
    }
  }, [appliedQuery, enqueueReportExport, exportSubmitting, hiddenMetrics]);

  const switchReportTab = (next: ReportTab) => {
    if (next === reportTab) return;
    const defaults = next === 'channel' ? CHANNEL_DEFAULT_DIMENSION_KEYS : UTM_DEFAULT_DIMENSION_KEYS;
    const groups = drillGroupsForReport(next);
    const groupOrder = groups.map(group => group.key);
    setReportTab(next);
    setDynamicDimensionKeys(ensureDynamicTimeDimension(defaults));
    setDrillDimensionKeys(orderedDrillKeys(defaults, groupOrder, groups));
    setDrillDraftKeys(defaults);
    setDrillGroupOrder(groupOrder);
    setDrillGroupOrderDraft(groupOrder);
    setDrillConfigOpen(false);
    setRows([]);
    setQueryError('');
    setQueried(false);
    pendingAutoQueryRef.current = true;
    setAutoQueryVersion(version => version + 1);
  };

  const switchTableTab = (next: TableTab) => {
    if (next === tableTab) return;
    // 树形响应按顶级节点分页，但一页会带完整子树；切到动态维度时不能
    // 暂时拿这批树节点逐行渲染，否则会在 50 条动态请求返回前阻塞浏览器。
    setTableTab(next);
    setRows([]);
    setPage(1);
    setTotal(0);
    setTotalPage(1);
    setQueryError('');
    setQueried(false);
    pendingAutoQueryRef.current = true;
    setAutoQueryVersion(version => version + 1);
  };

  const toggleMetric = (key: string) => setHiddenMetrics(current => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const selectTableView = (next: string[]) => {
    const nextViewID = next[0] ?? SYSTEM_VIEW_ID;
    const nextView = savedViews.find(view => view.id === nextViewID);
    setCurrentViewID(isRestoringView() ? SYSTEM_VIEW_ID : nextView?.id ?? SYSTEM_VIEW_ID);
    if (!isRestoringView()) setHiddenMetrics(new Set(nextView?.hiddenMetricKeys ?? []));
    if (brandId && siteId) window.localStorage.setItem(lastViewStorageKey, nextView?.id ?? SYSTEM_VIEW_ID);
  };

  const saveViews = (nextViews: SavedTableView[], nextViewID: string) => {
    setSavedViews(nextViews);
    setCurrentViewID(nextViewID);
    window.localStorage.setItem(viewsStorageKey, JSON.stringify(nextViews));
    window.localStorage.setItem(lastViewStorageKey, nextViewID);
  };

  const saveCurrentView = () => {
    if (!currentView) return;
    saveViews(savedViews.map(view => view.id === currentView.id ? { ...view, hiddenMetricKeys: [...hiddenMetrics] } : view), currentView.id);
    showToast('保存成功', 'success');
  };

  const saveAsView = () => {
    const name = viewNameDraft.trim();
    if (!name) {
      setViewNameError('请输入视图名称');
      return;
    }
    if (savedViews.some(view => view.name === name && view.id !== editingViewID)) {
      setViewNameError('该视图名称已存在，请更换名称');
      return;
    }
    if (editingViewID) {
      const editedView = savedViews.find(view => view.id === editingViewID);
      if (!editedView) return;
      saveViews(savedViews.map(view => view.id === editingViewID ? { ...view, name } : view), editingViewID);
      setSaveViewOpen(false);
      setEditingViewID(null);
      setViewNameDraft('');
      setViewNameError('');
      showToast('视图名称已修改', 'success');
      return;
    }
    const view: SavedTableView = { id: `view-${Date.now()}`, name, hiddenMetricKeys: [...hiddenMetrics] };
    saveViews([...savedViews, view], view.id);
    setSaveViewOpen(false);
    setViewNameDraft('');
    setViewNameError('');
    showToast('保存成功', 'success');
  };

  const openSaveAsView = () => {
    setEditingViewID(null);
    setViewNameDraft('');
    setViewNameError('');
    setSaveViewOpen(true);
  };

  const openRenameView = (view: SavedTableView) => {
    setEditingViewID(view.id);
    setViewNameDraft(view.name);
    setViewNameError('');
    setSaveViewOpen(true);
  };

  const shareTableView = async (view: SavedTableView) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('shared_view');
    const payload: SharedTableViewPayload = { version: 1, reportTab, view: { id: view.id, name: view.name, hiddenMetricKeys: view.hiddenMetricKeys } };
    url.searchParams.set('shared_view', JSON.stringify(payload));
    try {
      await navigator.clipboard.writeText(url.toString());
      showToast('复制链接成功，可进行分享', 'success');
    } catch {
      const input = document.createElement('textarea');
      input.value = url.toString();
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(input);
      showToast(copied ? '复制链接成功，可进行分享' : '复制链接失败，请检查浏览器权限', copied ? 'success' : 'error');
    }
  };

  const deleteTableView = async (view: SavedTableView) => {
    if (!await confirmDialog({ title: '删除视图', message: `确认删除视图「${view.name}」吗？此操作不可恢复。`, confirmText: '删除', danger: true })) return;
    const nextViews = savedViews.filter(item => item.id !== view.id);
    const nextViewID = currentViewID === view.id ? SYSTEM_VIEW_ID : currentViewID;
    saveViews(nextViews, nextViewID);
    if (nextViewID === SYSTEM_VIEW_ID) setHiddenMetrics(new Set());
    showToast('视图已删除', 'success');
  };

  const openDrillConfig = () => {
    setDrillDraftKeys([...new Set([...drillDimensionKeys, 'time'])]);
    setDrillGroupOrderDraft(normalizeDrillGroupOrder(drillGroupOrder, activeDrillGroups));
    setDrillConfigOpen(true);
  };

  const applyDrillConfig = () => {
    if (drillDraftKeys.length === 0) {
      return;
    }
    const nextGroupOrder = normalizeDrillGroupOrder(drillGroupOrderDraft, activeDrillGroups);
    setDrillGroupOrder(nextGroupOrder);
    setDrillDimensionKeys(orderedDrillKeys([...new Set([...drillDraftKeys, 'time'])], nextGroupOrder, activeDrillGroups));
    setDrillConfigOpen(false);
    setRows([]);
    setPage(1);
    setTotal(0);
    setTotalPage(1);
    setQueryError('');
    setQueried(false);
    pendingAutoQueryRef.current = true;
    setAutoQueryVersion(version => version + 1);
  };

  const toggleTreeNode = (id: string) => setExpandedTreeNodeIDs(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="page-section visible channel-wide-page">
      <div ref={filterAreaRef} className="dashboard-sticky-filters">
        <div className="data-filter-card channel-wide-filter-card">
          <div className="channel-wide-report-tabs" role="tablist" aria-label="报表口径">
            <button type="button" className={`channel-wide-report-tab${reportTab === 'channel' ? ' active' : ''}`} onClick={() => switchReportTab('channel')} role="tab" aria-selected={reportTab === 'channel'}>渠道-客群-目标</button>
            <button type="button" className={`channel-wide-report-tab${reportTab === 'utm' ? ' active' : ''}`} onClick={() => switchReportTab('utm')} role="tab" aria-selected={reportTab === 'utm'}>UTM-目标</button>
          </div>
          <div className="data-filter-row time channel-wide-time-row">
            <MarketingTimeRangeFilter
              grain={dateGrain}
              grains={DATE_GRAINS}
              onGrainChange={selectDateGrain}
              preset={datePreset}
              presets={CHANNEL_WIDE_RANGE_PRESETS}
              onPresetChange={selectDatePreset}
              start={customStart}
              end={customEnd}
              max={daysAgo(0)}
              customPreset="custom"
              onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }}
            />
            <div className="channel-wide-query-actions"><button type="button" className="btn btn-outline btn-sm" onClick={reset}><RotateCcw size={13} />重置</button><button type="button" className="btn btn-primary btn-sm" onClick={() => void query()} disabled={loading}>{loading ? <><Loader2 className="channel-wide-query-spinner" size={14} />查询中…</> : '查询'}</button></div>
          </div>
          <p className="channel-wide-filter-note">{reportTab === 'channel' ? '本表筛选项均为单选：一/二/三级渠道、一/二/三级客群、投放目标、投放目标明细。' : '本表筛选项均为单选；来源、媒介、Campaign 支持输入关键词模糊搜索。'}</p>
          <div className={`channel-wide-filter-grid ${reportTab === 'utm' ? 'utm' : ''}`}>
            {activeFilters.map(definition => <FilterSelect key={definition.key} definition={definition} value={filters[definition.key] ?? []} options={definition.key === 'channelL3' ? channelL3Options : definition.key === 'segmentL2' ? segmentL2Options : definition.key === 'segmentL3' ? segmentL3Options : undefined} onChange={value => updateFilter(definition.key, value)} />)}
          </div>
        </div>
      </div>

      <section className="channel-wide-panel">
        <div className="channel-wide-panel-head">
          <div>
            <div className="channel-wide-panel-title">渠道宽表</div>
            <div className="channel-wide-table-tabs" role="tablist" aria-label="表格交互方式">
              <button type="button" className={`channel-wide-table-tab${tableTab === 'tree' ? ' active' : ''}`} onClick={() => switchTableTab('tree')} role="tab" aria-selected={tableTab === 'tree'}>树形下钻</button>
              <button type="button" className={`channel-wide-table-tab${tableTab === 'dimension' ? ' active' : ''}`} onClick={() => switchTableTab('dimension')} role="tab" aria-selected={tableTab === 'dimension'}>动态维度</button>
            </div>
            <p className="channel-wide-panel-subtitle">{subtitle}</p>
          </div>
          <div className="channel-wide-panel-tools">
            <button type="button" className="btn btn-outline btn-sm" onClick={() => void exportFlatTable()} disabled={loading || exportSubmitting || !queried || rows.length === 0} title="导出当前筛选条件下的全部数据（不受列表分页限制，平铺 CSV）"><Download size={14} />{exportSubmitting ? '提交中…' : '导出'}</button>
            {exportError && <span className="channel-wide-export-error" role="status">{exportError}</span>}
            <span className="channel-wide-view-label">表格视图</span>
            <TableViewMenu currentViewID={currentViewID} views={savedViews} onSelect={viewID => selectTableView([viewID])} onSaveAs={openSaveAsView} onRename={openRenameView} onShare={view => void shareTableView(view)} onDelete={view => void deleteTableView(view)} />
            <ColumnToggle cols={COLUMN_TOGGLE_COLS} hiddenKeys={hiddenMetrics} onToggle={toggleMetric} onReset={() => setHiddenMetrics(new Set())} viewConfig={{ currentViewName: currentView?.name ?? '系统默认', isSystemView: !currentView, onSave: saveCurrentView, onSaveAs: openSaveAsView }} />
          </div>
        </div>

        {tableTab === 'tree' ? (
          <>
            <div className="channel-wide-drill-bar">
              <span className="channel-wide-drill-label">下钻路径</span><span className="channel-wide-drill-path">{treeDisplayDimensions.map(item => item.label).join(' → ') || '未选择维度'}</span>
              <div className="channel-wide-drill-actions"><button type="button" className="btn btn-outline btn-sm" onClick={openDrillConfig}>下钻配置</button><button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpandedTreeNodeIDs(new Set(expandableTreeNodeIDs))} disabled={expandableTreeNodeIDs.length === 0 || !canExpandAllTreeNodes} title={canExpandAllTreeNodes ? undefined : '节点过多，请逐级展开'}>全部展开</button><button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpandedTreeNodeIDs(new Set())} disabled={expandableTreeNodeIDs.length === 0}>全部收起</button></div>
            </div>
            {drillConfigOpen && <DrillConfig dimensions={activeDimensions} groups={activeDrillGroups} draftKeys={drillDraftKeys} groupOrder={drillGroupOrderDraft} mandatoryKeys={['time']} onChange={setDrillDraftKeys} onGroupOrderChange={setDrillGroupOrderDraft} onCancel={() => setDrillConfigOpen(false)} onApply={applyDrillConfig} onReset={() => { setDrillDraftKeys(defaultDimensionKeys); setDrillGroupOrderDraft(activeDrillGroups.map(group => group.key)); }} />}
          </>
        ) : (
          <div className="channel-wide-dimension-bar">
            <span className="channel-wide-drill-label">动态维度</span>
            <MultiSelect options={dynamicSelectableDimensions.map(item => ({ value: item.key, label: item.label, disabled: item.key === 'time' }))} value={orderedDynamicDimensionKeyList} onChange={updateDynamicDimensions} tagDisplay countLabelFormatter={count => `已选 ${count} 个维度`} style={{ width: 430 }} />
          </div>
        )}
        <DataTable tableTab={tableTab} reportTab={reportTab} dateGrain={appliedDateGrain} dimensions={tableTab === 'tree' ? treeDisplayDimensions : dynamicDimensions} hiddenMetrics={hiddenMetrics} rows={rows} treeRows={treeRows} expandedTreeNodeIDs={expandedTreeNodeIDs} onToggleTreeNode={toggleTreeNode} loading={loading} error={queryError} queried={queried} viewportHeight={tableViewport.height} pagination={shouldShowPagination ? (
          <Pagination
            className="channel-wide-pagination"
            page={page}
            totalPages={resolvedTotalPage}
            onChange={nextPage => void query(nextPage)}
            total={`共 ${total} 条`}
            pageSize={currentPageSize}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageSizeChange={nextPageSize => {
              setPageSizes(current => ({ ...current, [tableTab]: nextPageSize }));
              setPage(1);
              void query(1, nextPageSize);
            }}
            disabled={loading}
          />
        ) : undefined} />
      </section>
      {saveViewOpen && <SaveTableViewDialog title={editingViewID ? '修改视图名称' : '另存为视图'} confirmText={editingViewID ? '保存修改' : '保存'} value={viewNameDraft} error={viewNameError} onChange={value => { setViewNameDraft(value); setViewNameError(''); }} onCancel={() => { setSaveViewOpen(false); setEditingViewID(null); }} onConfirm={saveAsView} />}
    </div>
  );
}
